import convert from "convert-units";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import lround from "lodash/round";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import { DATE_FORMATS, PRIMARIESWITHOLYMPIC } from "../../../../constants";
import { CopyComponent } from "../../../../parts/CopyComponent";
import Select from "../../../../parts/inputs/select";
import { PasteExerciseComponent } from "../../../../parts/PasteExerciseComponent";
import SaveInputButtons from "../../../../parts/SaveInputButtons";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";
import { RillContext } from "../../../../rill/RillReact/share/base";
import addMetadata from "../../../../utils/add-metadata";
import {
  BUILDER_LIST,
  registerDropList,
  syncDraggables,
} from "../../../../utils/collapse-drag-controller";
import {
  checkOtherExercise,
  validateSet,
} from "../../../../utils/exercise-validations";
import {
  buildSupersets,
  computeSectionPrefixes,
  exerciseIdsOfRows,
  getHighestOrder,
  groupExercisesBySection,
  groupOptimisticMove,
  groupRowsFromElements,
  isSameGroup,
  moveSupersetAcrossLists,
  orderAmongSections,
  planSectionGroupDrop,
  reorderPreservingSupersets,
  rowsFromDraggables,
  sectionOrdersForIds,
  sectionOrdersFromRows,
  supersetChains,
} from "../../../../utils/prefix-calculator";
import { splitSections } from "../../../../utils/section-types";
import SectionSeparator from "../../calendar/components/EditMode/ExerciseEditor/SectionSeparator";
import WorkoutNoteBlock from "../../calendar/components/EditMode/ExerciseEditor/WorkoutNoteBlock";
import { AddExerciseProgram } from "./AddExerciseProgram";
import EditorSessionTableRow from "./EditorSessionTableRow";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

class EditorSessionTable extends React.Component {
  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      openQuickEdit: false,
      cycle: ctx.locals.params.cycleId,
      isEditing: ctx.locals.query.edit,
      program: ctx.locals.query.program,
      isProgramEditor: this.props.isProgramEditor || false,
      exercises: [],
      quickEditSessionId: 0,
      // Key (`program-week-session`) of the session whose "+ Workout note"
      // header pill was clicked, so its WorkoutNoteBlock opens in create mode.
      creatingWorkoutNoteKey: null,
    };
    this.loadInputs = [];
    this.intensityInputs = [];

    this.updateExerciseByState = this.updateExerciseByState.bind(this);
    this.handleAddSetLocally = this.handleAddSetLocally.bind(this);
    this.saveEdit = this.saveEdit.bind(this);
    this.saveQuickEdit = this.saveQuickEdit.bind(this);
    this.closeQuickEditMode = this.closeQuickEditMode.bind(this);
    this.openQuickEditMode = this.openQuickEditMode.bind(this);
    this.setEditing = this.setEditing.bind(this);
    this.mountDropList = this.mountDropList.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.draggables = new Map();
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.isProgramEditor && !isEqual(nextState, this.state)) {
      return true;
    }
    if (
      this.props.isProgramEditor &&
      this.props.week.length !== nextProps.week.length
    ) {
      return true;
    }
    if (!isEqual(this.props.session, nextProps.session)) {
      return true;
    }

    if (
      !isEqual(this.props.exercisesInClipboard, nextProps.exercisesInClipboard)
    ) {
      return true;
    }

    if (
      this.props.isProgramEditor &&
      nextProps.opacity === this.props.opacity &&
      isEqual(nextProps.exerciseList, this.props.exerciseList)
    ) {
      return false;
    }
    return true;
  }

  componentDidMount() {
    // Fixes MSB-947
    this._scrollTimeout = setTimeout(() => {
      const hash = this.props.scrollTo;

      if (hash) {
        const elementToScroll = document.getElementById(hash);
        if (elementToScroll) {
          elementToScroll.scrollIntoView({ behavior: "smooth" });
        }
      }
    }, 1000);
    const { isEditing } = this.state;
    const {
      session: { week },
      sessionIndex,
      readOnly = false,
      programOrder,
    } = this.props;

    if (
      isEditing &&
      week === this.context.locals.query.weekIndex &&
      sessionIndex === this.context.locals.query.sessionIndex
    ) {
      this.setState({ openQuickEdit: true }, () => {
        this._editScrollTimeout = setTimeout(() => {
          const elementToScroll = document.getElementById(
            this.context.locals.query.editExerciseId
          );

          if (elementToScroll) elementToScroll.scrollIntoView(false);
        });
      });
    }
    const selector = `.exercises-list.program-${programOrder || 0}-workout-${
      this.props.session.order
    }-list.week-${this.props.session.week}`;

    const element = document.querySelector(selector);
    if (element && !readOnly) {
      this.mountDropList(element);
      syncDraggables(element, this.draggables);
    }
  }

  componentWillUnmount() {
    clearTimeout(this._scrollTimeout);
    clearTimeout(this._editScrollTimeout);
    this.unregisterDropList?.();
    this.unregisterDropList = null;
    for (const entry of this.draggables.values()) entry.destroy();
    this.draggables.clear();
  }

  mountDropList(element) {
    this.unregisterDropList?.();
    this.listElement = element;
    this.unregisterDropList = registerDropList(
      element,
      (event) => this.handleDrop(event),
      BUILDER_LIST,
      () => {
        const ids = Array.from(element.children, (row) => row.id);
        return buildSupersets(
          this.context.locals.editExercises
            .filter((exercise) => ids.includes(exercise._id))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
      }
    );
  }

  handleDrop(e) {
    const { editExercises } = this.context.locals;
    const { cycle, session, programOrder } = this.props;
    const { item, from, to, oldIndex, newIndex } = e;
    const fromWeek = from.getAttribute("data-week");
    const fromSession = from.getAttribute("data-session");
    const toWeek = to.getAttribute("data-week");
    const toSession = to.getAttribute("data-session");
    const sameList = fromWeek === toWeek && fromSession === toSession;

    // Section group (header + every row under it): the controller leaves the
    // DOM alone, so the new layout is computed from the rows as data.
    if (e.group) {
      const sessionRows = (list) =>
        groupRowsFromElements(
          list.querySelectorAll(":scope > li, :scope > .section-separator"),
          editExercises
        );
      const { fromRows, toRows, optimistic } = planSectionGroupDrop(
        item,
        sessionRows(from),
        newIndex,
        sameList ? null : sessionRows(to)
      );

      const fromIds = exerciseIdsOfRows(fromRows);
      let body = {
        cycle: cycle._id,
        program: programOrder,
        session: session.order,
        week: session.week,
        exercises: fromIds,
        sections: sectionOrdersFromRows(fromRows),
      };
      if (sameList)
        body.supersets = supersetChains(
          fromIds
            .map((id) => editExercises.find((entry) => entry._id === id))
            .filter(Boolean)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
          fromIds
        );
      else
        body = {
          ...body,
          toExercises: exerciseIdsOfRows(toRows),
          toWeek,
          toSession,
          toSections: sectionOrdersFromRows(toRows),
        };

      submit("/actions/exercise/reorder", { method: "POST", body });
      return optimistic;
    }

    if (oldIndex === newIndex && sameList) return;

    // Revert list matches the draggable selector so oldIndex/newIndex (which
    // count separators too) map correctly back onto the DOM.
    const fromDraggable = from.querySelectorAll(
      ":scope > li, :scope > .section-separator"
    );

    // Reposition a section separator, within its session or into another
    // one (mirrors DayEditor's cross-day move), by computing a fractional
    // order between its new exercise neighbors. Exercises untouched.
    if (item.classList.contains("section-separator")) {
      // Undo Sortable's DOM move; persisted orders drive the layout.
      const revert = () =>
        from.insertBefore(
          item,
          sameList
            ? fromDraggable[oldIndex + (oldIndex > newIndex)]
            : fromDraggable[oldIndex]
        );

      // Dropped directly on top of another section (no exercise between the
      // two separators)? A plain midpoint would tie their orders and leave
      // one section owning zero exercises (banished to the bottom). Instead,
      // swap the two sections' orders so the headers exchange places while
      // the exercises under each stay put.
      const adjacentSeparator =
        (item.previousElementSibling?.classList.contains("section-separator") &&
          item.previousElementSibling) ||
        (item.nextElementSibling?.classList.contains("section-separator") &&
          item.nextElementSibling) ||
        null;

      if (sameList && adjacentSeparator) {
        const allSections = this.context.locals.editSections || [];
        const draggedId = item.getAttribute("data-section-id");
        const targetId = adjacentSeparator.getAttribute("data-section-id");
        const orderOfSection = (id) =>
          allSections.find((s) => s._id === id)?.order;
        const draggedOrder = orderOfSection(draggedId);
        const targetOrder = orderOfSection(targetId);

        revert();

        if (
          draggedId &&
          targetId &&
          draggedOrder != null &&
          targetOrder != null
        ) {
          // The reorder backend overwrites this session's exercise list with
          // what we send, so resend the current order (complexes expanded)
          // unchanged and only swap the two section orders.
          const orderedExerciseIds = [];
          const liItems = from.querySelectorAll(":scope > li");
          for (let i = 0; i < liItems.length; i++) {
            const exercise = editExercises.find(
              (ex) => ex._id === liItems[i].id
            );
            if (exercise?.complex && exercise?.complexInstance) {
              const complexIds = editExercises
                .filter(
                  (e) =>
                    e.complex === exercise.complex &&
                    e.complexInstance === exercise.complexInstance
                )
                .sort((e1, e2) => e1.indexInComplex - e2.indexInComplex)
                .map((e) => e._id);
              orderedExerciseIds.push(...complexIds);
            } else {
              orderedExerciseIds.push(liItems[i].id);
            }
          }

          submit("/actions/exercise/reorder", {
            method: "POST",
            body: {
              cycle: cycle._id,
              program: programOrder,
              session: session.order,
              week: session.week,
              exercises: orderedExerciseIds,
              sections: [
                { _id: draggedId, order: targetOrder },
                { _id: targetId, order: draggedOrder },
              ],
            },
          });
        }
        return;
      }

      // Cross-session neighbors aren't in this session's exerciseList, but
      // editExercises spans the whole cycle.
      const neighborExercises = sameList
        ? this.props.exerciseList || []
        : editExercises;
      const orderOf = (el) =>
        el ? neighborExercises.find((ex) => ex._id === el.id)?.order : null;

      let prevEl = item.previousElementSibling;
      while (prevEl && prevEl.nodeName !== "LI")
        prevEl = prevEl.previousElementSibling;
      let nextEl = item.nextElementSibling;
      while (nextEl && nextEl.nodeName !== "LI")
        nextEl = nextEl.nextElementSibling;

      const { editExercises: allExercises } = this.context.locals;
      const prevExercise = prevEl
        ? allExercises.find((ex) => ex._id === prevEl.id)
        : null;
      const nextExercise = nextEl
        ? allExercises.find((ex) => ex._id === nextEl.id)
        : null;

      if (isSameGroup(prevExercise, nextExercise)) {
        revert();
        submit("/actions/common/toast", {
          method: "POST",
          body: {
            type: "error",
            message: this.props.t(
              "athlete-portal:programs.cannotDropSectionInGroup"
            ),
          },
        });
        return;
      }

      const prevOrder = orderOf(prevEl);
      const nextOrder = orderOf(nextEl);
      let newOrder = null;
      if (prevOrder != null && nextOrder != null)
        newOrder = (prevOrder + nextOrder) / 2;
      else if (prevOrder != null) newOrder = prevOrder + 0.5;
      else if (nextOrder != null) newOrder = nextOrder - 0.5;

      // Target workout has no exercises (empty, or only sections): order it
      // among the neighboring separators, or first if there are none.
      if (newOrder == null && !sameList) newOrder = orderAmongSections(item);

      revert();

      if (newOrder != null) {
        const athleteId = this.context.locals.query?.athlete || "";
        const organizationCoach = this.getOrganizationCoach();
        let updateQuery = `?proxyAthlete=${athleteId}&reqSource=program`;
        if (organizationCoach) {
          updateQuery += `&organizationCoach=${organizationCoach}`;
        }
        submit(
          `/actions/exercise-section/update/${item.getAttribute(
            "data-section-id"
          )}${updateQuery}`,
          {
            method: "POST",
            body: sameList
              ? { order: newOrder }
              : {
                  order: newOrder,
                  programWeek: toWeek,
                  programSession: toSession,
                },
          }
        );
      }
      return newOrder != null;
    }

    const exercises = [];
    const toExercises = [];

    const fromItems = from.querySelectorAll(":scope > li");
    const toItems = to.querySelectorAll(":scope > li");
    // Capture the combined (exercises + separators) lists in their dropped
    // order before the revert below, so section orders can be recomputed
    // relative to the new exercise layout. querySelectorAll is static, so
    // the order survives the DOM revert.
    const toDraggable = sameList
      ? fromDraggable
      : to.querySelectorAll(":scope > li, :scope > .section-separator");
    for (let i = 0; i < fromItems.length; i++) {
      const element = fromItems[i];
      const exercise = editExercises.find((exercise) => {
        return exercise._id === element.id;
      });

      if (exercise?.complex && exercise?.complexInstance) {
        const complexExercises = editExercises
          .filter(
            (e) =>
              e.complex === exercise.complex &&
              e.complexInstance === exercise.complexInstance
          )
          .sort((e1, e2) => e1.indexInComplex - e2.indexInComplex)
          .map((e) => e._id);
        exercises.push(...complexExercises);
      } else {
        exercises.push(element.id);
      }
    }
    if (!sameList) {
      for (let i = 0; i < toItems.length; i++) {
        const element = toItems[i];
        const exercise = editExercises.find((exercise) => {
          return exercise._id === element.id;
        });

        if (exercise?.complex && exercise?.complexInstance) {
          const complexExercises = editExercises
            .filter(
              (e) =>
                e.complex === exercise.complex &&
                e.complexInstance === exercise.complexInstance
            )
            .sort((e1, e2) => e1.indexInComplex - e2.indexInComplex)
            .map((e) => e._id);
          toExercises.push(...complexExercises);
        } else {
          toExercises.push(element.id);
        }
      }
      from.insertBefore(item, fromDraggable[oldIndex]);
    } else {
      from.insertBefore(item, fromDraggable[oldIndex + (oldIndex > newIndex)]);
    }

    // Keep supersets/complexes atomic: the drag moves one row, so move the
    // whole group together and never split another group on drop-in.
    // editExercises is the whole cycle sorted by a per-session `order`, so
    // re-scope per session (sorted by order) before deriving groups.
    // See reorderPreservingSupersets in prefix-calculator.
    const originalOrder = (ids) =>
      ids
        .map((id) => editExercises.find((e) => e._id === id))
        .filter(Boolean)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    let body = {
      cycle: cycle._id,
      program: programOrder,
      session: session.order,
      week: session.week,
    };

    if (sameList) {
      body.exercises = reorderPreservingSupersets(
        originalOrder(exercises),
        exercises,
        item.id
      );
      body.supersets = supersetChains(originalOrder(exercises), body.exercises);
      // The DOM has one row per complex; the order list has every exercise.
      body.sections = sectionOrdersForIds(
        rowsFromDraggables(fromDraggable),
        body.exercises
      );
    } else {
      // Cross-session: carry the dragged row's whole group to the
      // destination, kept grouped, and remove it from the source.
      const { fromIds, toIds, groupIds } = moveSupersetAcrossLists(
        originalOrder([...exercises, item.id]),
        originalOrder(toExercises.filter((id) => id !== item.id)),
        item.id,
        exercises,
        toExercises
      );
      body.exercises = fromIds;
      body.sections = sectionOrdersForIds(
        rowsFromDraggables(fromDraggable),
        fromIds
      );
      body = {
        ...body,
        toExercises: toIds,
        toWeek,
        toSession,
        toSections: sectionOrdersForIds(
          rowsFromDraggables(toDraggable),
          toIds,
          groupIds
        ),
      };
    }

    submit("/actions/exercise/reorder", {
      method: "POST",
      body,
    });

    // Show the dragged group (a lone row, superset or complex) where the save
    // will put it, which is not always the slot under the pointer.
    return groupOptimisticMove(
      originalOrder(sameList ? exercises : [...exercises, item.id]),
      item.id,
      sameList ? body.exercises : body.toExercises
    );
  }

  componentDidUpdate() {
    const {
      session: { order, week },
      programOrder,
      readOnly = false,
    } = this.props;
    const selector = `.exercises-list.program-${
      programOrder || 0
    }-workout-${order}-list.week-${week}`;
    const element = document.querySelector(selector);
    if (element && !readOnly) {
      // Only re-register when the list node itself changed.
      if (this.listElement !== element) this.mountDropList(element);
      syncDraggables(element, this.draggables);
    }
  }

  openQuickEditMode(e) {
    e.preventDefault();
    this.setState((prevState) => ({
      ...prevState,
      openQuickEdit: true,
      quickEditSessionId: prevState.openQuickEdit
        ? prevState.quickEditSessionId
        : prevState.quickEditSessionId + 1,
    }));
    this.props.workoutOpacityCallback({ type: "add", keyId: this.props.keyId });
  }

  closeQuickEditMode(e) {
    e?.preventDefault();
    document.getElementById("quick-edit").reset();

    this.setState({ exercises: [], openQuickEdit: false, isEditing: false });
    this.props.workoutOpacityCallback({
      type: "remove",
      keyId: this.props.keyId,
    });
  }

  handleQuickEditChange(e, exId, setId) {
    let {
      target: { name, value },
    } = e;
    const { locals } = this.context;
    const exercises = cloneDeep(this.state.exercises);
    const exerciseList = cloneDeep(this.props.exerciseList);
    const exerciseIndex = exercises.findIndex(
      (ex) => ex._id.toString() === exId
    );
    const exerciseListIndex = exerciseList.findIndex(
      (ex) => ex._id.toString() === exId
    );
    const exercise =
      exercises[exerciseIndex] ||
      cloneDeep(get(this.context.locals, "editExercises", [])).find(
        (ex) => ex._id === exId
      ) ||
      cloneDeep(get(this.context.locals, "activeExercises", [])).find(
        (ex) => ex._id === exId
      ) ||
      {};

    const set = get(exercise, ["sets", setId], false);
    if (["load", "percentRepMax", "loadDrop"].includes(name)) {
      if (String(value).includes(",")) {
        value = e.target.value = String(value).replace(",", ".");
      }
    }

    if (set && set[name] !== value && name !== "exerciseType") {
      set[name] = value;
      const primary = get(exercise, "primary");
      const repMax =
        exercise.type === "secondary"
          ? exercise.secondaryRepMaximum
          : get(
              exercise,
              `repMaximums.${primary}`,
              get(this.props.activeUser, `repMaximums.${primary}`, 0)
            );

      // ensure load and percentRepMax are in sync
      if (name === "load") {
        if (this.context.locals.activeUser.units === "imperial") {
          set.load = lround(convert(value).from("lb").to("kg"), 2);
        }
        if (exercise.type !== "assistant") {
          set.percentRepMax = lround((set.load / repMax) * 100, 1);
        }
      }

      if (name === "percentRepMax") {
        set.load = lround((repMax * set.percentRepMax) / 100, 2);
      }

      const loadInput = this.loadInputs[`${exId}_${setId}`];
      if (loadInput) {
        if (name === "percentRepMax") {
          let loadToShow = set.load;
          if (this.context.locals.activeUser.units === "imperial") {
            loadToShow = lround(convert(loadToShow).from("kg").to("lb"), 2);
          }
          loadInput.value = loadToShow;
        }
        if (!this.intensityInputs[`${exId}_${setId}`]) {
          this.intensityInputs[`${exId}_${setId}`] = { value: 0 };
        }
        this.intensityInputs[`${exId}_${setId}`].value = set.percentRepMax ?? 0;
      }

      const validatedSet = validateSet({
        exercise,
        set,
        locals,
        field: name,
      });
      if (validatedSet) {
        exercise.sets[setId] = validatedSet;

        // Handle complex exercises - update other exercises in the same complex
        if (
          exercise.complex &&
          exercise.complexInstance &&
          ["sets", "rpe", "load", "percentRepMax"].includes(name)
        ) {
          // Find all exercises in the same complex from all available sources
          const allAvailableExercises = [
            ...exercises,
            ...cloneDeep(get(this.context.locals, "editExercises", [])),
            ...cloneDeep(get(this.context.locals, "activeExercises", [])),
          ];

          // Remove duplicates based on _id
          const uniqueExercises = allAvailableExercises.reduce((acc, ex) => {
            if (
              !acc.find(
                (existing) => existing._id.toString() === ex._id.toString()
              )
            ) {
              acc.push(ex);
            }
            return acc;
          }, []);

          // Find all exercises in the same complex
          const complexExercises = uniqueExercises.filter(
            (ex) =>
              ex.complex === exercise.complex &&
              ex.complexInstance === exercise.complexInstance &&
              ex._id.toString() !== exId.toString() // Exclude the current exercise
          );

          // Update each exercise in the complex
          complexExercises.forEach((complexEx) => {
            let complexExIndex = exercises.findIndex(
              (ex) => ex._id.toString() === complexEx._id.toString()
            );
            let complexExListIndex = exerciseList.findIndex(
              (ex) => ex._id.toString() === complexEx._id.toString()
            );

            // If the complex exercise doesn't exist in exercises array, add it
            if (complexExIndex === -1) {
              exercises.push(cloneDeep(complexEx));
              complexExIndex = exercises.length - 1;
            }

            // If the complex exercise doesn't exist in exerciseList array, add it
            if (complexExListIndex === -1) {
              exerciseList.push(cloneDeep(complexEx));
              complexExListIndex = exerciseList.length - 1;
            }

            if (
              complexExIndex !== -1 &&
              exercises[complexExIndex].sets?.[setId]
            ) {
              // Update the corresponding set
              const complexSet = exercises[complexExIndex].sets[setId];
              complexSet[name] = value;

              // Validate the complex exercise set
              const validatedComplexSet = validateSet({
                exercise: exercises[complexExIndex],
                set: complexSet,
                locals,
                field: name,
              });

              if (validatedComplexSet) {
                exercises[complexExIndex].sets[setId] = validatedComplexSet;
                // Also update the exerciseList
                exerciseList[complexExListIndex] = exercises[complexExIndex];
              }
            }
          });
        }

        this.setExersicesStates(
          exercise,
          exercises,
          exerciseIndex,
          exerciseList,
          exerciseListIndex
        );
      } else {
        // revert to original value
        const originalSet = cloneDeep(
          get(this.context.locals, "editExercises", [])
        ).find((ex) => ex._id === exId)?.sets?.[setId];
        document.getElementById(`${exId}_${setId}_${name}`).value =
          originalSet?.[name] ?? "";
      }
    } else if (name === "notes") {
      // Have to add a space " " in the empty state "" because if not rill gets rid
      // of them when passing it to the backend (interprets it as false)
      exercise.notes = value === "" ? " " : value;

      this.setExersicesStates(
        exercise,
        exercises,
        exerciseIndex,
        exerciseList,
        exerciseListIndex
      );
    } else if (name === "repsPerSide" || name === "tempo") {
      exercise[name] = value;

      this.setExersicesStates(
        exercise,
        exercises,
        exerciseIndex,
        exerciseList,
        exerciseListIndex
      );
    } else if (name === "exerciseType") {
      const { movement, repMaximum, newPrimary } = value;
      const sets = get(exercise, "sets", false);

      addMetadata(
        exercise,
        movement,
        newPrimary,
        this.context.locals.activeUserToRequest.primaryExercises,
        this.context.locals.activeUserToRequest.exerciseLibrary
      );
      // if the movement chosen is a primary, change to a primary
      if (PRIMARIESWITHOLYMPIC.includes(movement)) {
        // setting new values
        exercise.type = "primary";
        exercise.primary = movement;
        // resetting secondary and secondary RM
        exercise.secondary = "null";
        exercise.secondaryRepMaximum = 0;
        exercise.parameters = "null";
        // updating the values of each set in the exercise
        sets.forEach((currentSet, idx) => {
          if (
            currentSet.load &&
            currentSet.percentRepMax &&
            currentSet.load >= 0 &&
            currentSet.percentRepMax >= 0
          ) {
            currentSet.load = lround(
              (repMaximum * currentSet.percentRepMax) / 100,
              2
            );
            currentSet.percentRepMax = lround(
              (currentSet.load / repMaximum) * 100,
              1
            );
          }

          // updating the load shown
          const loadInput = this.loadInputs[`${exId}_${idx}`];
          if (loadInput) {
            let loadToShow = currentSet.load;
            const { units } = this.context.locals.activeUser;

            if (units === "imperial") {
              loadToShow = lround(convert(loadToShow).from("kg").to("lb"), 2);
            }
            loadInput.value = loadToShow;
          }
          // Making sure no values are invalid after any changes
          if (!currentSet.load || currentSet.load === null) currentSet.load = 0;
          if (!currentSet.percentRepMax || currentSet.percentRepMax === null)
            currentSet.percentRepMax = 0;
          if (!currentSet.sets) currentSet.sets = 1;
          if (!currentSet.reps) currentSet.reps = 1;
          if (!currentSet.load && !currentSet.rpe) currentSet.rpe = 5;
        });
      }
      // isAssistant = true then it is assistant
      else if (newPrimary === "assistant") {
        exercise.type = "assistant";
        exercise.primary = "assistant";
        exercise.secondary = movement;
        exercise.secondaryRepMaximum = 0;
        if (value.parameters) exercise.parameters = value.parameters;
      }
      // else its a secondary
      else {
        // change type from primary/assistant to secondary and set new values
        exercise.type = "secondary";
        exercise.primary = newPrimary;
        exercise.secondary = movement;
        exercise.secondaryRepMaximum = repMaximum;
        exercise.parameters = "null";
        // updating the values of each set in the exercise
        sets.forEach((currentSet, idx) => {
          if (
            currentSet.load &&
            currentSet.percentRepMax &&
            currentSet.load >= 0 &&
            currentSet.percentRepMax >= 0
          ) {
            currentSet.load = lround(
              (repMaximum * currentSet.percentRepMax) / 100,
              2
            );
            currentSet.percentRepMax = lround(
              (currentSet.load / repMaximum) * 100,
              1
            );
          }

          // updating the load shown
          const loadInput = this.loadInputs[`${exId}_${idx}`];
          if (loadInput) {
            let loadToShow = currentSet.load;
            const { units } = this.context.locals.activeUser;

            if (units === "imperial") {
              loadToShow = lround(convert(loadToShow).from("kg").to("lb"), 2);
            }
            loadInput.value = loadToShow;
          }
          // Making sure no values are invalid after any changes
          if (!currentSet.load || currentSet.load === null) currentSet.load = 0;
          if (!currentSet.percentRepMax || currentSet.percentRepMax === null)
            currentSet.percentRepMax = 0;
          if (!currentSet.sets) currentSet.sets = 1;
          if (!currentSet.reps) currentSet.reps = 1;
          if (!currentSet.load && !currentSet.rpe) currentSet.rpe = 5;
        });
      }

      // Buffer the movement change into state.exercises like every other
      // quick-edit field edit (sets/reps/rpe/load/notes/etc.) instead of
      // auto-saving it immediately - it should only persist, with a single
      // notification, when the coach clicks Quick Edit's own Save button.
      this.setExersicesStates(
        exercise,
        exercises,
        exerciseIndex,
        exerciseList,
        exerciseListIndex
      );
    }
  }

  setExersicesStates(
    exercise,
    exercises,
    exerciseIndex,
    exerciseList,
    exerciseListIndex,
    saveAfterChange = false
  ) {
    // if exercise already exists in state
    if (exerciseIndex > -1) {
      exercises[exerciseIndex] = exercise;
      exerciseList[exerciseListIndex] = exercise;
    }
    // otherwise push to exercises
    if (exerciseIndex < 0) {
      exercises.push(exercise);
    }
    this.setState(
      { exercises },
      saveAfterChange ? () => this.saveEdit(false) : undefined
    );
  }

  // Adds a newly-entered set straight into the quick-edit local state and
  // auto-saves, instead of POSTing to /exercise/quick-edit/sets/add directly.
  // The direct POST used to race the whole-form saveEdit PUT: both write the
  // full exercise document, and whichever lands last in Mongo wins, silently
  // dropping the other's changes. Routing through the same
  // setExersicesStates/saveEdit path as every other quick-edit change
  // guarantees a single writer.
  handleAddSetLocally(
    exId,
    setIndex,
    newSet,
    complexExerciseIds = [],
    repsTwo,
    repsThree
  ) {
    const exercises = cloneDeep(this.state.exercises);
    const exerciseList = cloneDeep(this.props.exerciseList);
    const exerciseIndex = exercises.findIndex((ex) => ex._id === exId);
    const exerciseListIndex = exerciseList.findIndex((ex) => ex._id === exId);
    const exercise =
      exercises[exerciseIndex] ||
      cloneDeep(get(this.context.locals, "editExercises", [])).find(
        (ex) => ex._id === exId
      ) ||
      cloneDeep(get(this.context.locals, "activeExercises", [])).find(
        (ex) => ex._id === exId
      ) ||
      {};

    exercise.sets.splice(parseInt(setIndex, 10) + 1, 0, newSet);

    complexExerciseIds.forEach((complexExId) => {
      const complexIndex = exercises.findIndex((ex) => ex._id === complexExId);
      let complexExercise = exercises[complexIndex];

      if (!complexExercise) {
        complexExercise =
          cloneDeep(get(this.context.locals, "editExercises", [])).find(
            (ex) => ex._id === complexExId
          ) ||
          cloneDeep(get(this.context.locals, "activeExercises", [])).find(
            (ex) => ex._id === complexExId
          );
        if (complexExercise) exercises.push(complexExercise);
      }

      if (complexExercise) {
        const complexSet = { ...newSet };
        if (complexExercise.indexInComplex === 1)
          complexSet.reps = repsTwo || newSet.reps;
        if (complexExercise.indexInComplex === 2)
          complexSet.reps = repsThree || newSet.reps;
        complexExercise.sets.splice(parseInt(setIndex, 10) + 1, 0, complexSet);
      }
    });

    this.setExersicesStates(
      exercise,
      exercises,
      exerciseIndex,
      exerciseList,
      exerciseListIndex,
      true
    );
  }

  saveEdit(close = true) {
    const { exercises } = this.state;

    // An unreadable RPE ("8-7") is never emitted into state (the cell would
    // be refreshed away by validateSet), so state only holds its last valid
    // prefix ("8"). The cell's own error is the only record of it.
    const rpeError = document
      .querySelector("#quick-edit [data-rpe-error]")
      ?.getAttribute("data-rpe-error");
    if (rpeError) {
      this.context.locals.flash("error", rpeError);
      submit("/actions/refresh");
      return;
    }

    if (
      exercises !== undefined &&
      exercises.length > 0 &&
      checkOtherExercise(exercises)
    ) {
      if (this.props.isProgramEditor) {
        this.props.updateExercises(exercises);
      }

      let body = {
        exercises,
        omitModified: true,
        omitActiveUser: true,
      };

      const localsContext = this.context.locals;
      if (localsContext.activeUser.organizationId) {
        const organizationCoach = localsContext.activeUserToRequest._id;
        body = { ...body, organizationCoach };
      }

      submit(
        `/actions/exercise/quick-edit?proxyAthlete=${get(
          this.context.locals,
          "query.athlete"
        )}`,
        {
          method: "POST",
          body,
        }
      );
    }
    if (close) this.closeQuickEditMode();
  }

  saveQuickEdit(e) {
    e.preventDefault();
    this.saveEdit();
  }

  updateExerciseByState(exercise, index) {
    const copyExercises = [...this.state.exercises];
    copyExercises[index] = exercise;
    this.setState({ exercises: copyExercises }, this.saveEdit);
  }

  setEditing(isEditing = true) {
    this.setState({ isEditing });
  }

  // The organization owner's id — set when the acting coach is a subcoach,
  // so section create/update/delete requests scope to the template's real
  // owner instead of the subcoach's own (empty) sections.
  getOrganizationCoach() {
    const { activeUser, activeUserToRequest } = this.context.locals;
    return activeUser.organizationId ? activeUserToRequest._id : undefined;
  }

  render() {
    const {
      session,
      sessionIndex,
      week,
      fromMetric,
      activeUser,
      cycle,
      program,
      programOrder,
      altClass,
      column,
      updateExercise,
      deleteExercise,
      updateOrderExercises,
      opacity,
      readOnly = false,
    } = this.props;

    const order = session.order;
    const cycleId = cycle._id;
    const { openQuickEditMode } = this;
    const isUser =
      cycle.type === "user" ||
      cycle.type === "coach" ||
      cycle.type === "subscription";
    const activeUserToUpdateSessions =
      this.context.locals.athlete || this.context.locals.activeUser;
    const activeUserToHandleCopyPasteAction =
      this.context.locals.athlete || this.context.locals.activeUserToRequest;
    const isCoach =
      this.context.locals.pathname.includes("athlete-portal") ||
      this.context.locals.pathname.includes("coaching-portal");

    // Sections (text separators) scoped to this program session.
    const exerciseListForSections = this.props.exerciseList || [];
    const sessionSections = (this.context.locals.editSections || []).filter(
      (s) =>
        s.cycle &&
        String(s.cycle.id) === String(cycleId) &&
        s.cycle.program === programOrder &&
        s.cycle.programWeek === session.week &&
        s.cycle.programSession === order
    );
    // The workout note is rendered separately, above the exercises list (see
    // the <WorkoutNoteBlock> below) — pull it out of `sessionSections` so it
    // never enters the separator-grouping/ordering logic below.
    // `sessionSections` (unsplit) is still passed to EditWorkoutNameComponent,
    // which forwards it to CopyComponent so copying the session carries the
    // note along.
    const { sections: normalSessionSections, note: sessionNote } =
      splitSections(sessionSections);
    // Sections share the exercise order space, so the "next order" for a new
    // section/import/paste must account for both. -1 when empty → first section
    // lands at -0.5.
    const highestOrder = getHighestOrder(
      exerciseListForSections,
      normalSessionSections
    );
    const sectionPrefixes = computeSectionPrefixes(
      exerciseListForSections,
      normalSessionSections
    );
    const sectionByFirstExerciseId = {};
    // Per-section last-row membership: used to hide the "Add Superset" button on
    // the last exercise of a section so a superset can't span a section boundary.
    const sectionLastRowId = {};
    const emptySections = [];
    groupExercisesBySection(
      exerciseListForSections,
      normalSessionSections
    ).forEach((group) => {
      if (group.exercises.length) {
        sectionLastRowId[group.exercises[group.exercises.length - 1]._id] =
          true;
      }
      if (!group.section) return;
      if (group.exercises.length) {
        sectionByFirstExerciseId[group.exercises[0]._id] = group.section;
      } else {
        emptySections.push(group.section);
      }
    });
    // Empty sections must keep their ordered position, not get dumped at the end.
    // Each renders just before the first exercise whose order >= its own (that
    // row always leads a section); sections after the last exercise stay trailing.
    const emptySectionsBeforeId = {};
    const trailingEmptySections = [];
    if (emptySections.length) {
      const sortedRows = [...exerciseListForSections].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      );
      emptySections.forEach((section) => {
        const before = sortedRows.find(
          (row) => (row.order ?? 0) >= (section.order ?? 0)
        );
        if (before) {
          if (!emptySectionsBeforeId[before._id])
            emptySectionsBeforeId[before._id] = [];
          emptySectionsBeforeId[before._id].push(section);
        } else {
          trailingEmptySections.push(section);
        }
      });
    }
    const sectionAthleteId = this.context.locals.query?.athlete;
    const organizationCoach = this.getOrganizationCoach();
    const workoutNoteKey = `${programOrder}-${session.week}-${order}`;
    const isCreatingWorkoutNote =
      !sessionNote && this.state.creatingWorkoutNoteKey === workoutNoteKey;

    const notSingleWorkout = cycle.storeType !== "single-workout";
    const sessions = program?.sessions;

    if (
      session?.exercises?.length > 0 ||
      emptySections.length > 0 ||
      sessionNote
    ) {
      return (
        <li
          className="workout"
          key={`workout_${sessionIndex}_${column}`}
          style={{ opacity: opacity ? 0.3 : 1 }}
        >
          <header
            className={`${
              cycle.storeType === "single-workout" ? "top-0!" : ""
            } ${readOnly ? "team-read-only" : ""}`}
          >
            {notSingleWorkout ? (
              <EditWorkoutNameComponent
                cycleId={cycleId}
                session={session}
                order={order}
                programNumber={this.state.program}
                activeUserToUpdateSessions={activeUserToUpdateSessions}
                showCopyButton={true}
                isCoach={isCoach}
                activeUserToHandleCopyPasteAction={
                  activeUserToHandleCopyPasteAction
                }
                exerciseList={
                  this.props.ungroupedExercises || this.props.exerciseList
                }
                isTeams={
                  cycle.storeType === "teams" ||
                  (cycle.storeType === "flexible" &&
                    (cycle.trainingSplitType || "open") !== "open")
                }
                sessions={sessions}
                isReadOnly={readOnly}
                sections={sessionSections}
                onAddWorkoutNote={
                  !readOnly && !sessionNote && !isCreatingWorkoutNote
                    ? () =>
                        this.setState({
                          creatingWorkoutNoteKey: workoutNoteKey,
                        })
                    : undefined
                }
                t={this.props.t}
              />
            ) : (
              <>
                <p className="s2">
                  {this.props.t("athlete-portal:programs.singleWorkout")}
                </p>
                {isCoach && (
                  <div className="flex items-center gap-1">
                    <ToolTip
                      id={`copy-workout-tooltip-${session.week}-${order}`}
                      body={this.props.t("athlete-portal:programs.copyWorkout")}
                      float={false}
                      place="bottom"
                    >
                      <CopyComponent
                        className="bg-(--secondary-background-color) w-7 h-7! rounded-lg"
                        exercises={
                          this.props.ungroupedExercises ||
                          this.props.exerciseList
                        }
                        athlete={activeUserToHandleCopyPasteAction}
                        workoutNameSuffix={
                          session.workoutName
                            ? String(session.workoutName).replace(
                                `${this.props.t(
                                  "athlete-portal:programs.workoutN",
                                  { n: order }
                                )} - `,
                                ""
                              )
                            : ""
                        }
                        sections={sessionSections}
                      />
                    </ToolTip>
                  </div>
                )}
              </>
            )}
          </header>
          <div
            className={`exercises-frame ${
              this.state.openQuickEdit && "quick-edit"
            }`}
          >
            <form id="quick-edit">
              <ul
                className={`exercises-list program-${
                  programOrder || 0
                }-workout-${order}-list week-${session.week}`}
                ref="exercises"
                key={cycleId}
                data-week={session.week}
                data-session={order}
              >
                <WorkoutNoteBlock
                  note={sessionNote}
                  athleteId={sectionAthleteId}
                  reqSource="program"
                  organizationCoach={organizationCoach}
                  editable={!readOnly}
                  creating={isCreatingWorkoutNote}
                  createBody={{
                    cycleId,
                    program: programOrder,
                    programWeek: session.week,
                    programSession: order,
                  }}
                  onCancelCreate={() =>
                    this.setState({ creatingWorkoutNoteKey: null })
                  }
                />
                {this.props.exerciseList.map((exercise, i) => {
                  return (
                    <React.Fragment key={exercise._id}>
                      {(emptySectionsBeforeId[exercise._id] || []).map(
                        (section) => (
                          <SectionSeparator
                            key={section._id}
                            section={section}
                            athleteId={sectionAthleteId}
                            reqSource="program"
                            organizationCoach={organizationCoach}
                            editable={!readOnly}
                            dragHandleClass="grabber cursor-grab drag-icon"
                            groupDrag
                          />
                        )
                      )}
                      {sectionByFirstExerciseId[exercise._id] && (
                        <SectionSeparator
                          // Key on the section id (not position) so swapping
                          // which section leads an exercise remounts the
                          // separator with fresh local state instead of reusing
                          // the old section's (e.g. a stale name in the input).
                          key={sectionByFirstExerciseId[exercise._id]._id}
                          section={sectionByFirstExerciseId[exercise._id]}
                          athleteId={sectionAthleteId}
                          reqSource="program"
                          organizationCoach={organizationCoach}
                          editable={!readOnly}
                          dragHandleClass="grabber cursor-grab drag-icon"
                          groupDrag
                        />
                      )}
                      <EditorSessionTableRow
                        isProgramEditor={this.state.isProgramEditor}
                        exercises={this.props.exerciseList}
                        exercise={exercise._id}
                        sectionPrefix={sectionPrefixes[exercise._id]}
                        isSectionLast={!!sectionLastRowId[exercise._id]}
                        exerciseOrder={i}
                        fromMetric={fromMetric}
                        activeUser={activeUser}
                        cycle={cycle}
                        program={program}
                        programOrder={programOrder}
                        session={session}
                        sessionIndex={sessionIndex}
                        altClass={altClass}
                        openQuickEditMode={openQuickEditMode}
                        handleQuickEditChange={(e, exId, setId) =>
                          this.handleQuickEditChange(e, exId, setId)
                        }
                        handleAddSetLocally={this.handleAddSetLocally}
                        loadInputs={this.loadInputs}
                        intensityInputs={this.intensityInputs}
                        isQuickEditOpen={this.state.openQuickEdit}
                        updateExercise={updateExercise}
                        deleteExercise={deleteExercise}
                        updateOrderExercises={updateOrderExercises}
                        exercisesInState={this.state.exercises}
                        updateExerciseByState={this.updateExerciseByState}
                        setEditing={this.setEditing}
                        stateExercises={this.state.exercises}
                        quickEditSessionId={this.state.quickEditSessionId}
                        isCoach={isCoach}
                        readOnly={readOnly}
                        ungroupedExercises={this.props.ungroupedExercises}
                        updateExercises={this.props.updateExercises}
                      />
                    </React.Fragment>
                  );
                })}
                {trailingEmptySections.map((section) => (
                  <SectionSeparator
                    key={section._id}
                    section={section}
                    athleteId={sectionAthleteId}
                    reqSource="program"
                    organizationCoach={organizationCoach}
                    editable={!readOnly}
                    dragHandleClass="grabber cursor-grab drag-icon"
                    groupDrag
                  />
                ))}
                {/* <EditorSessionTableRow /> */}
              </ul>
            </form>
            {!readOnly ? (
              <footer
                className={
                  this.state.openQuickEdit ? "justify-end" : "justify-between"
                }
              >
                {this.state.openQuickEdit ? (
                  <>
                    <button
                      onClick={this.closeQuickEditMode}
                      data-button-type="secondary"
                      className="text-(--danger)! discard"
                      type="button"
                    >
                      {this.props.t("athlete-portal:programs.discard")}
                    </button>
                    <button
                      onClick={this.saveQuickEdit}
                      data-button-type="primary"
                      type="button"
                    >
                      {this.props.t("common:save")}
                    </button>
                  </>
                ) : (
                  <>
                    <a
                      data-button-type="text"
                      role="button"
                      href={`/actions/refresh?modal[type]=add-exercise&programWeek=${
                        session.week
                      }&programSession=${sessionIndex + 1}&editMode=false`}
                      className="pl-1"
                    >
                      <SvgIcon
                        src="/img/new-icons/add-gradient-icon.svg"
                        className="h-5 w-5"
                        noFilterOnLight
                      />
                      <span className="font-semibold">
                        {this.props.t("athlete-portal:programs.addExercise")}
                      </span>
                    </a>
                    <div className="flex items-center gap-1">
                      <ToolTip
                        id="add-section-tooltip"
                        body={this.props.t(
                          "athlete-portal:exerciseEditor.addSection"
                        )}
                        float={false}
                        place="bottom"
                      >
                        <a
                          data-button-type="secondary"
                          role="button"
                          href="#_"
                          onClick={(e) => {
                            e.preventDefault();
                            let createQuery = `?proxyAthlete=${
                              sectionAthleteId || ""
                            }&reqSource=program`;
                            if (organizationCoach) {
                              createQuery += `&organizationCoach=${organizationCoach}`;
                            }
                            submit(
                              `/actions/exercise-section/create${createQuery}`,
                              {
                                method: "POST",
                                body: {
                                  name: this.props.t(
                                    "athlete-portal:exerciseEditor.newSection"
                                  ),
                                  // Sit just below all current content (exercises
                                  // and sections) so it becomes the bottommost.
                                  order: highestOrder + 0.5,
                                  cycleId,
                                  program: programOrder,
                                  programWeek: session.week,
                                  programSession: order,
                                },
                              }
                            );
                          }}
                        >
                          <SvgIcon src="/img/new-icons/workout-sections-icon.svg" />
                        </a>
                      </ToolTip>
                      {notSingleWorkout && (
                        <ToolTip
                          id="import-workout-tooltip"
                          body={this.props.t(
                            "athlete-portal:programs.importWorkout"
                          )}
                          float={false}
                          place="bottom"
                        >
                          <a
                            data-button-type="secondary"
                            role="button"
                            href={`/actions/refresh?modal[type]=import-workout&programWeek=${
                              session.week
                            }&programSession=${
                              sessionIndex + 1
                            }&exercisesLength=${
                              this.props.exerciseList?.length || 0
                            }&highestOrder=${highestOrder}`}
                          >
                            <SvgIcon src="/img/new-icons/import.svg" />
                          </a>
                        </ToolTip>
                      )}
                      <PasteExerciseComponent
                        athlete={activeUserToHandleCopyPasteAction}
                        date={moment().format(DATE_DAY_FORMAT)}
                        query={this.props.exercisesInClipboard}
                        haveExercises={true}
                        isProgram={true}
                        cycle={cycle}
                        sessionProgram={session}
                        programNumber={this.state.program}
                        exercisesLength={this.props.exerciseList?.length}
                        highestOrder={highestOrder}
                      />

                      {notSingleWorkout && (
                        <ToolTip
                          id="workout-progression-tooltip"
                          body={this.props.t(
                            "athlete-portal:programs.workoutProgression"
                          )}
                          float={false}
                          place="bottom"
                        >
                          <a
                            href={`/actions/refresh?modal[type]=progression&type=workout&week=${
                              session.week
                            }&session=${
                              sessionIndex + 1
                            }&program=${programOrder}&cycle=${cycleId}`}
                            data-button-type="secondary"
                            role="button"
                          >
                            <SvgIcon src="/img/new-icons/percentage-square.svg" />
                          </a>
                        </ToolTip>
                      )}

                      <ToolTip
                        id="quick-edit-tooltip"
                        body={this.props.t("athlete-portal:programs.quickEdit")}
                        float={false}
                        place="bottom"
                      >
                        <button
                          onClick={this.openQuickEditMode}
                          data-button-type="secondary"
                          type="button"
                        >
                          <SvgIcon src="/img/new-icons/edit-pencil.svg" />
                        </button>
                      </ToolTip>

                      {notSingleWorkout && (
                        <ToolTip
                          id="duplicate-workout-tooltip"
                          body={this.props.t(
                            "athlete-portal:programs.duplicateWorkout"
                          )}
                          float={false}
                          place="bottom"
                        >
                          <a
                            href={`/actions/refresh?modal[type]=duplicate-workout&week=${
                              session.week
                            }&session=${sessionIndex + 1}&isExercise=${false}`}
                            data-button-type="secondary"
                            role="button"
                          >
                            <SvgIcon src="/img/new-icons/duplicate.svg" />
                          </a>
                        </ToolTip>
                      )}

                      {notSingleWorkout && (
                        <ToolTip
                          id="move-down-tooltip"
                          body={this.props.t(
                            "athlete-portal:programs.moveDown"
                          )}
                          float={false}
                          place="bottom"
                        >
                          <a
                            href={`/actions/cycle/reorder-workout/${cycleId}/${programOrder}/${
                              session.week
                            }/${order}/${order + 1}`}
                            data-button-type="secondary"
                            role="button"
                            aria-disabled={sessionIndex === week.length - 1}
                          >
                            <SvgIcon src="/img/new-icons/arrow-square-down.svg" />
                          </a>
                        </ToolTip>
                      )}

                      {notSingleWorkout && (
                        <ToolTip
                          id="move-up-tooltip"
                          body={this.props.t("athlete-portal:programs.moveUp")}
                          float={false}
                          place="bottom"
                        >
                          <a
                            href={`/actions/cycle/reorder-workout/${cycleId}/${programOrder}/${
                              session.week
                            }/${order}/${order - 1}`}
                            data-button-type="secondary"
                            role="button"
                            aria-disabled={sessionIndex === 0}
                          >
                            <SvgIcon src="/img/new-icons/arrow-square-up.svg" />
                          </a>
                        </ToolTip>
                      )}

                      {isUser && week.length > 1 && notSingleWorkout && (
                        <ToolTip
                          id="delete-edit-workout"
                          body={this.props.t(
                            "athlete-portal:programs.deleteWorkout"
                          )}
                          float={false}
                          place="bottom"
                        >
                          <a
                            href={`/actions/cycle/delete-workout/${cycleId}/${programOrder}/${session.week}/${order}`}
                            data-button-type="secondary"
                            role="button"
                          >
                            <SvgIcon
                              src="/img/new-icons/red-trash.svg"
                              noFilterOnLight
                            />
                          </a>
                        </ToolTip>
                      )}

                      {isUser && week.length === 1 && notSingleWorkout && (
                        <ToolTip
                          id="delete-week-tooltip"
                          body={this.props.t(
                            "athlete-portal:programs.deleteWeekTooltip"
                          )}
                          float={false}
                          place="bottom"
                        >
                          <a
                            href={`/actions/cycle/delete-week/${cycleId}/${programOrder}/${session.week}`}
                            data-button-type="secondary"
                            role="button"
                          >
                            <SvgIcon
                              src="/img/new-icons/red-trash.svg"
                              noFilterOnLight
                            />
                          </a>
                        </ToolTip>
                      )}

                      {isUser && week.length === 1 && !notSingleWorkout && (
                        <ToolTip
                          id="delete-single-workout-tooltip"
                          body={this.props.t(
                            "athlete-portal:programs.clearWorkout"
                          )}
                          float={false}
                          place="bottom"
                        >
                          <a
                            href={`/actions/cycle/delete-single-workout/${cycleId}/`}
                            data-button-type="secondary"
                            role="button"
                          >
                            <SvgIcon
                              src="/img/new-icons/red-trash.svg"
                              noFilterOnLight
                            />
                          </a>
                        </ToolTip>
                      )}
                    </div>
                  </>
                )}
              </footer>
            ) : (
              <footer className="min-h-[47.75px] border-hidden!"></footer>
            )}
          </div>
        </li>
      );
    }
    return (
      <li className="workout alt-add" key={`workout_${sessionIndex}_${column}`}>
        <header>
          {notSingleWorkout ? (
            <EditWorkoutNameComponent
              cycleId={cycleId}
              session={session}
              order={order}
              programNumber={this.state.program}
              activeUserToUpdateSessions={activeUserToUpdateSessions}
              showCopyButton={false}
              isCoach={isCoach}
              activeUserToHandleCopyPasteAction={
                activeUserToHandleCopyPasteAction
              }
              exerciseList={
                this.props.ungroupedExercises || this.props.exerciseList
              }
              isReadOnly={readOnly}
              isTeams={
                cycle.storeType === "teams" ||
                (cycle.storeType === "flexible" &&
                  (cycle.trainingSplitType || "open") !== "open")
              }
              firstDayOfWeek={cycle.firstDayOfWeek || 0}
              sessions={sessions}
              t={this.props.t}
            />
          ) : (
            <p className="s2">
              {this.props.t("athlete-portal:programs.singleWorkout")}
            </p>
          )}
        </header>
        <ul
          className={`exercises-list program-${
            programOrder || 0
          }-workout-${order}-list week-${session.week} ${
            readOnly ? "empty-read-only" : "empty"
          }`}
          ref="exercises"
          key={cycleId}
          data-week={session.week}
          data-session={order}
          style={{ minHeight: 0, height: 0 }}
        />
        <div className={`exercises-frame alt-add workout-${order}-list`}>
          {!readOnly ? (
            <AddExerciseProgram
              session={session}
              sessionIndex={sessionIndex}
              activeUserToHandleCopyPasteAction={
                activeUserToHandleCopyPasteAction
              }
              program={this.state.program}
              cycle={cycle}
              exercisesInClipboard={this.props.exercisesInClipboard}
              date={moment().format(DATE_DAY_FORMAT)}
              programOrder={programOrder}
              sectionAthleteId={sectionAthleteId}
              organizationCoach={organizationCoach}
            />
          ) : (
            <p className="z-1">
              {this.props.t("athlete-portal:programs.noExercisesForWorkout")}
            </p>
          )}
        </div>
        {!readOnly ? (
          <footer className="justify-end">
            {notSingleWorkout && (
              <ToolTip
                id={`move-next-${sessionIndex}-${session.week}-tooltip`}
                body={this.props.t("athlete-portal:programs.moveDown")}
                float={false}
                place="bottom"
              >
                <a
                  data-button-type="secondary"
                  role="button"
                  href={`/actions/cycle/reorder-workout/${cycleId}/${programOrder}/${
                    session.week
                  }/${order}/${order + 1}`}
                  aria-disabled={sessionIndex === week.length - 1}
                >
                  <SvgIcon src="/img/new-icons/arrow-square-down.svg" />
                </a>
              </ToolTip>
            )}

            {notSingleWorkout && (
              <ToolTip
                id={`move-prev-${sessionIndex}-${session.week}-tooltip`}
                body={this.props.t("athlete-portal:programs.moveUp")}
                float={false}
                place="bottom"
              >
                <a
                  data-button-type="secondary"
                  role="button"
                  href={`/actions/cycle/reorder-workout/${cycleId}/${programOrder}/${
                    session.week
                  }/${order}/${order - 1}`}
                  aria-disabled={sessionIndex === 0}
                >
                  <SvgIcon src="/img/new-icons/arrow-square-up.svg" />
                </a>
              </ToolTip>
            )}

            {isUser && week.length > 1 && (
              <ToolTip
                id={`delete-workout-${sessionIndex}-${session.week}-tooltip`}
                body={this.props.t("athlete-portal:programs.deleteWorkout")}
                float={false}
                place="bottom"
              >
                <a
                  data-button-type="secondary"
                  role="button"
                  href={`/actions/cycle/delete-workout/${cycleId}/${programOrder}/${session.week}/${order}`}
                >
                  <SvgIcon src="/img/new-icons/red-trash.svg" noFilterOnLight />
                </a>
              </ToolTip>
            )}
            {isUser && week.length === 1 && cycle?.storeType !== "teams" && (
              <ToolTip
                id={`delete-week-${sessionIndex}-${session.week}-tooltip`}
                body={this.props.t("athlete-portal:programs.deleteWeekTooltip")}
                float={false}
                place="bottom"
              >
                <a
                  data-button-type="secondary"
                  role="button"
                  href={`/actions/cycle/delete-week/${cycleId}/${programOrder}/${session.week}`}
                  aria-disabled={!notSingleWorkout}
                >
                  <SvgIcon src="/img/new-icons/red-trash.svg" noFilterOnLight />
                </a>
              </ToolTip>
            )}
          </footer>
        ) : (
          <footer className="min-h-[47.75px] border-hidden!"></footer>
        )}
      </li>
    );
  }
}

EditorSessionTable.contextType = RillContext;

export default withTranslation(["athlete-portal", "common"])(
  EditorSessionTable
);

/**
 * Render the edit workout name component
 * @param {*} param0
 * @returns
 */

const EditWorkoutNameComponent = ({
  cycleId,
  session,
  order,
  programNumber,
  activeUserToUpdateSessions,
  showCopyButton,
  isCoach,
  activeUserToHandleCopyPasteAction,
  exerciseList,
  isTeams,
  sessions,
  isReadOnly,
  sections,
  // Set only when the session has no workout note yet (and isn't read only):
  // shows the "+ Workout note" pill next to the copy icon.
  onAddWorkoutNote,
  t,
}) => {
  const workoutBaseName = t
    ? t("athlete-portal:programs.workoutN", { n: order })
    : `Workout ${order}`;
  const suffixFromSession = session.workoutName
    ? String(session.workoutName).replace(`${workoutBaseName} - `, "")
    : "";
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingWorkoutDay, setIsEditingWorkoutDay] = useState(false);
  const [editableWorkoutName, setEditableWorkoutName] = useState("");
  const [workoutDay, setWorkoutDay] = useState(session?.workoutDay || 0);

  const startingCalendarDayOptions = t
    ? [
        { text: t("athlete-portal:programs.days.sunday"), value: 0 },
        { text: t("athlete-portal:programs.days.monday"), value: 1 },
        { text: t("athlete-portal:programs.days.tuesday"), value: 2 },
        { text: t("athlete-portal:programs.days.wednesday"), value: 3 },
        { text: t("athlete-portal:programs.days.thursday"), value: 4 },
        { text: t("athlete-portal:programs.days.friday"), value: 5 },
        { text: t("athlete-portal:programs.days.saturday"), value: 6 },
      ]
    : [
        { text: "Sunday", value: 0 },
        { text: "Monday", value: 1 },
        { text: "Tuesday", value: 2 },
        { text: "Wednesday", value: 3 },
        { text: "Thursday", value: 4 },
        { text: "Friday", value: 5 },
        { text: "Saturday", value: 6 },
      ];

  /**
   * function to assign the value of the input to the state
   */
  const handleInputWorkoutNameChange = ({ target: { value } }) => {
    setEditableWorkoutName(value);
  };

  /**
   * Submit function
   * @param {*} e
   */
  const handleEditWorkoutName = (e) => {
    e.preventDefault();

    const updatedWorkoutName =
      editableWorkoutName.length > 0 ? `${editableWorkoutName}`.trim() : "";

    const body = {
      updatedWorkoutName,
    };

    submit(
      `/actions/cycle/edit/program-workout-name/${cycleId}?program=${programNumber}&athlete=${activeUserToUpdateSessions}&sessionOrder=${session.order}&sessionWeek=${session.week}`,
      {
        method: "POST",
        body,
      }
    );

    setIsEditing(false);
  };

  // Session workout day is not updating for the other sessions
  // useEffect needed cuz I didn't find any other way, I'm a hack
  useEffect(() => {
    if (!isEditingWorkoutDay) {
      setWorkoutDay(session.workoutDay);
    }
  }, [session.workoutDay]);

  const handleEditWorkoutDay = (val) => {
    if (workoutDay === val) return;

    setIsEditingWorkoutDay(true);

    const alreadyUsedWorkoutDay =
      (!!sessions && sessions.find((s) => s.workoutDay === Number(val))) ||
      false;

    const body = {
      programOrder: programNumber,
      sessionOrder: session.order,
      workoutDay: Number(val),
      switching: Boolean(alreadyUsedWorkoutDay),
      ...(alreadyUsedWorkoutDay && {
        sessionOrderSwitch: alreadyUsedWorkoutDay.order,
        sessionOrderSwitchValue: workoutDay,
      }),
    };

    submit(`/actions/cycle/edit/program-workout-day/${cycleId}`, {
      method: "POST",
      body,
    });

    setWorkoutDay(val);

    // delay or else it won't trigger properly
    setTimeout(() => setIsEditingWorkoutDay(false), 500);
  };

  return (
    <>
      {!isEditing ? (
        <>
          <p className="s2">
            {suffixFromSession
              ? `${workoutBaseName} - ${suffixFromSession}`.trim()
              : workoutBaseName}
          </p>
          <div className="flex items-center gap-1">
            {showCopyButton && isCoach && (
              <ToolTip
                id="copy-workout-tooltip"
                body={
                  t ? t("athlete-portal:programs.copyWorkout") : "Copy workout"
                }
                float={false}
                place="bottom"
              >
                <CopyComponent
                  className="bg-(--secondary-background-color) w-7 h-7! rounded-lg"
                  exercises={exerciseList}
                  athlete={activeUserToHandleCopyPasteAction}
                  workoutNameSuffix={suffixFromSession}
                  sections={sections}
                />
              </ToolTip>
            )}
            {onAddWorkoutNote && (
              <button
                type="button"
                data-button-type="secondary"
                className="workout-note-add-pill"
                onClick={onAddWorkoutNote}
              >
                <SvgIcon src="/img/new-icons/add.svg" />
                {t
                  ? t("athlete-portal:exerciseEditor.addWorkoutNote")
                  : "Instructions"}
              </button>
            )}
            {!isReadOnly && (
              <ToolTip
                id="edit-workout-name-tooltip"
                body={
                  t
                    ? t("athlete-portal:programs.editWorkoutName")
                    : "Edit workout name"
                }
                float={false}
                place="bottom"
              >
                <button
                  data-button-type="secondary"
                  type="button"
                  className="p-0 size-7 rounded-lg"
                  onClick={() => {
                    setEditableWorkoutName(suffixFromSession);
                    setIsEditing(true);
                  }}
                >
                  <SvgIcon src="/img/new-icons/edit-pencil.svg" />
                </button>
              </ToolTip>
            )}
            {isTeams && (
              <Select
                name={"workoutDay"}
                options={startingCalendarDayOptions}
                value={workoutDay}
                onChange={(newVal) => handleEditWorkoutDay(newVal)}
                disabled={isReadOnly}
                overlay={true}
              />
            )}
          </div>
        </>
      ) : (
        <div className="workout-name-input-div">
          <p className="s2">{workoutBaseName}</p>
          {suffixFromSession ? <p className="s2"> - </p> : null}
          <input
            name="workoutName"
            placeholder={suffixFromSession}
            value={editableWorkoutName}
            onChange={handleInputWorkoutNameChange}
          />
          <SaveInputButtons
            enabled={isEditing}
            handleSave={handleEditWorkoutName}
            handleDismiss={(e) => {
              e.preventDefault();
              setIsEditing(false);
            }}
            saveDisabled={editableWorkoutName === suffixFromSession}
          />
        </div>
      )}
    </>
  );
};
